import { useEffect, useState, useCallback, useRef } from 'react';
import { useViewer } from '../../../contexts/ViewerContext';
import { useLoadingPlan } from '../../../contexts/LoadingPlanContext';
import config from '../../../config';
import * as THREE from 'three';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { enqueueSnackbar } from 'notistack';
import {
  buildLockedLayerData,
  buildLockedPalletChildrenMap,
  buildPartialRenestPayload,
  buildUpdatedLayerData,
  filterToLockedParts,
  isSupportPartId,
  removeChildFromPallets,
} from './partialRenest.utils';
import { computeVoxelBoundaryNudge, fromMatrix4, getTruckBoundsWithFoundation } from './editMode.utils';
import loadingPlansService from '../../../services/api/loadingPlansService';
import { ReplacePartModal } from './ReplacePartModal/ReplacePartModal';
import { InvalidPlacementDialog } from './InvalidPlacementDialog';
import { setPalletChildrenVisibility, usePartLoading } from './hooks/usePartLoading';
import { usePartNavigation } from './hooks/usePartNavigation';
import { useEditMode } from './hooks/useEditMode';
import { useRenestQueue } from './hooks/useRenestQueue';
import { usePartsOrientations } from './hooks/usePartsOrientations';
import './ViewerComponent.scss';

const INVALID_POSITION_REASONS =
  'The part collides with another part, is outside the trailer, lacks support underneath, or cannot be reached for loading.';
const ORIENTATION_UNFIT_REASON =
  "In this orientation the part would hang below the level it rests on, so it can't be supported anywhere here. Try another orientation.";

interface ViewerComponentProps {
  onStepChange?: (currentStep: number, totalSteps: number) => void;
  onPartsLoaded?: (parts: any[]) => void;
  handleNextStepCallback?: () => void;
  handlePrevStepCallback?: () => void;
  showNavigation?: boolean;
  navigationCommand?: 'next' | 'prev' | null;
  onNavigationComplete?: () => void;
  isStartFromFirstPart?: boolean;
  onRecalculatingChange?: (isRecalculating: boolean) => void;
}

export function ViewerComponent({ onStepChange, onPartsLoaded, handleNextStepCallback, handlePrevStepCallback, showNavigation = false, navigationCommand, onNavigationComplete, isStartFromFirstPart = false, onRecalculatingChange }: ViewerComponentProps) {
  const { viewer, initialize } = useViewer();
  const { currentLoadingPlan, updateCurrentLoadingPlan } = useLoadingPlan();
  // Declared here (not inside usePartNavigation) because usePartLoading's resetSceneForReload
  // also needs these setters, and usePartLoading is wired before usePartNavigation below.
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [activePartId, setActivePartId] = useState<string | null>(null);

  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [isInvalidPlacementDialogOpen, setIsInvalidPlacementDialogOpen] = useState(false);

  const currentPartRef = useRef<THREE.Object3D | null>(null);
  const hasInitializedVisibilityRef = useRef(false);
  const isRenestInFlightRef = useRef(false);
  const submitRenestTriggerRef = useRef<((trigger: any) => Promise<void>) | null>(null);
  const lastBatchFirstPartIdRef = useRef<string | null>(null);
  const restoreToPartIdRef = useRef<string | null>(null);
  const replacementAcceptedRef = useRef(false);

  const getTransformControls = useCallback(() => {
    return viewer ? (viewer as any).transformControls : null;
  }, [viewer]);

  const getTruckBoundsForEdit = useCallback(() => {
    if (!viewer) return null;
    return getTruckBoundsWithFoundation(viewer.getScene());
  }, [viewer]);

  const setPartColor = useCallback((part: THREE.Object3D, color: number) => {
    part.traverse((child) => {
      if ((child as THREE.Mesh).isMesh && (child as THREE.Mesh).material) {
        ((child as THREE.Mesh).material as THREE.MeshPhongMaterial).color.setHex(color);
      }
    });
  }, []);

  const {
    layerData,
    setLayerData,
    isLoadingParts,
    scenePartsMap,
    setScenePartsMap,
    palletChildrenMap,
    setPalletChildrenMap,
    loadSinglePart,
    getPartFromScene,
    resetSceneForReload,
  } = usePartLoading({
    viewer,
    currentLoadingPlan,
    getTransformControls,
    currentPartRef,
    hasInitializedVisibilityRef,
    replacementAcceptedRef,
    isRenestInFlightRef,
    restoreToPartIdRef,
    setActivePartId,
    setCurrentStep,
    setCurrentLayer,
    setTotalSteps,
  });

  const { getPartsOrientations, ensurePartsOrientations, isLoadingPartsOrientations } = usePartsOrientations(
    currentLoadingPlan?.id
  );

  const {
    isEditMode,
    isPartIntersecting,
    isOrientationUnfit,
    isLoadingPartPositions,
    editOrientationAngle,
    editOrientationStep,
    editOrientationTotal,
    hasEngineOrientations,
    editPartRef,
    editIsIntersectingRef,
    editCurrentOrientationIndexRef,
    editGridRef,
    replacedSlotInfoRef,
    handleOrientationNext,
    handleOrientationPrev,
    handleEnterEditMode,
    exitEditMode,
    cancelOrientationAnimation,
    handleConfirmReplace,
  } = useEditMode({
    viewer,
    currentLoadingPlan,
    scenePartsMap,
    setScenePartsMap,
    palletChildrenMap,
    getPartFromScene,
    loadSinglePart,
    currentPartRef,
    setActivePartId,
    replacementAcceptedRef,
    getTransformControls,
    getTruckBoundsForEdit,
    setPartColor,
    isReplaceModalOpen,
    getPartsOrientations,
    layerData,
    currentLayer,
  });

  // The orientations request can outlive the render the click came from, so enter through the latest handler.
  const handleEnterEditModeRef = useRef(handleEnterEditMode);
  handleEnterEditModeRef.current = handleEnterEditMode;
  const handleEditClick = useCallback(async () => {
    if (await ensurePartsOrientations()) {
      handleEnterEditModeRef.current();
    }
  }, [ensurePartsOrientations]);

  const {
    isStepChanging,
    isCurrentPartSupport,
    currentPartNestedInfo,
    currentPartDbMeta,
    handleNextStep,
    handlePrevStep,
  } = usePartNavigation({
    viewer,
    currentLoadingPlan,
    layerData,
    scenePartsMap,
    palletChildrenMap,
    isLoadingParts,
    isEditMode,
    setPartColor,
    currentPartRef,
    hasInitializedVisibilityRef,
    restoreToPartIdRef,
    replacementAcceptedRef,
    lastBatchFirstPartIdRef,
    submitRenestTriggerRef,
    isStartFromFirstPart,
    onStepChange,
    handleNextStepCallback,
    handlePrevStepCallback,
    navigationCommand,
    onNavigationComplete,
    currentStep,
    setCurrentStep,
    totalSteps,
    setTotalSteps,
    currentLayer,
    setCurrentLayer,
    activePartId,
    setActivePartId,
  });

  const { isRecalculating, submitRenestTrigger, buildUpdatedNestedParts } = useRenestQueue({
    currentLoadingPlan,
    updateCurrentLoadingPlan,
    layerData,
    currentLayer,
    scenePartsMap,
    resetSceneForReload,
    currentPartRef,
    isEditMode,
    isRenestInFlightRef,
    submitRenestTriggerRef,
    lastBatchFirstPartIdRef,
  });

  useEffect(() => {
    onRecalculatingChange?.(isRecalculating);
    return () => onRecalculatingChange?.(false);
  }, [isRecalculating, onRecalculatingChange]);

  const performAccept = useCallback(async () => {
    cancelOrientationAnimation(true);

    const part = editPartRef.current;
    if (!part) {
      exitEditMode(false);
      return;
    }

    const replacedSlot = replacedSlotInfoRef.current;
    const finalPartId = part.uuid;
    // A part on an engine grid cell is already exactly placed; the boundary nudge is only for the mesh-test fallback.
    if (!editGridRef.current && !isSupportPartId(finalPartId)) {
      part.updateMatrixWorld(true);
      const nudge = computeVoxelBoundaryNudge(new THREE.Box3().setFromObject(part));
      if (nudge.lengthSq() > 0) {
        part.matrix.premultiply(new THREE.Matrix4().makeTranslation(nudge.x, nudge.y, nudge.z));
        part.matrixWorldNeedsUpdate = true;
        part.updateMatrixWorld(true);
      }
    }
    const finalMatrix = part.matrix.clone();
    const finalOrientationIndex = editCurrentOrientationIndexRef.current;
    const swap = {
      replacedOldPartId: replacedSlot?.oldPartId,
      finalPartId,
      finalMatrix: fromMatrix4(finalMatrix),
      finalOrientationIndex,
    };

    replacementAcceptedRef.current = true;
    exitEditMode(false);

    // The edit invalidates everything placed after it: keep the parts up to the edited one (locked) and let the
    // engine re-nest the rest around the new placement.
    const rawNestedPartsBeforeEdit = currentLoadingPlan?.nestingOutput?.nested_parts;
    const lockedPayload = rawNestedPartsBeforeEdit
      ? buildPartialRenestPayload({
          rawNestedParts: rawNestedPartsBeforeEdit,
          layerData,
          currentLayer,
          editedPartId: finalPartId,
          newTransformMatrix: swap.finalMatrix,
          replacedOldPartId: replacedSlot?.oldPartId || null,
          scenePartsMap,
          editedOrientationIndex: finalOrientationIndex,
        })
      : [];
    const lockedPartIds = new Set(lockedPayload.map((p) => p.part_id));

    if (lockedPayload.length > 0) {
      // Pallets are not in the payload but their locked children are, so they stay in the plan.
      const lockedLayerData = buildLockedLayerData(
        layerData,
        lockedPartIds,
        scenePartsMap,
        palletChildrenMap,
        setPalletChildrenVisibility,
        swap,
      );
      setLayerData(lockedLayerData);
      setPalletChildrenMap(
        buildLockedPalletChildrenMap(palletChildrenMap, lockedPartIds, replacedSlot ? finalPartId : null)
      );

      // Steps are layer entries (a pallet is one step), same as when the plan is first loaded.
      const newTotalSteps = Object.values(lockedLayerData).reduce((sum, layer) => sum + layer.parts.length, 0);
      setTotalSteps(newTotalSteps);
      setCurrentStep(newTotalSteps - 1);
      onStepChange?.(newTotalSteps - 1, newTotalSteps);
    } else {
      // Nothing to lock against (the edited part is not in the current layer): just record the edit.
      const updatedLayerData = buildUpdatedLayerData(layerData, swap);
      setLayerData(updatedLayerData);
      if (replacedSlot) {
        setPalletChildrenMap(removeChildFromPallets(palletChildrenMap, finalPartId));
      }
      const newTotalSteps = Object.values(updatedLayerData).reduce((sum, layer) => sum + layer.parts.length, 0);
      if (newTotalSteps !== totalSteps) {
        setTotalSteps(newTotalSteps);
      }
    }

    const updatedNestedParts = buildUpdatedNestedParts({
      fromPartId: replacedSlot?.oldPartId || finalPartId,
      toPartId: finalPartId,
      matrix: finalMatrix,
      orientationIndex: finalOrientationIndex,
    });

    // Truncate nested_parts to the locked prefix so the plan matches what is shown while re-nesting.
    const updatedNestingOutput = currentLoadingPlan?.nestingOutput
      ? {
          ...currentLoadingPlan.nestingOutput,
          nested_parts: (lockedPayload.length > 0
            ? filterToLockedParts(updatedNestedParts, lockedPartIds)
            : updatedNestedParts) as any,
        }
      : undefined;

    if (updatedNestingOutput) {
      updateCurrentLoadingPlan({
        nestingOutput: updatedNestingOutput,
      });
    }

    // Saved (and awaited) before re-nesting: the re-nest recognises its own result by the plan's updatedAt
    // moving, which this save also does. If the re-nest fails, the edit is still kept.
    let isSaved = true;
    if (currentLoadingPlan?.id && updatedNestingOutput) {
      try {
        await loadingPlansService.updateLoadingPlan(currentLoadingPlan.id, {
          nestingOutput: updatedNestingOutput,
        });
      } catch (err) {
        isSaved = false;
        console.error('Failed to save updated loading plan to server:', err);
        enqueueSnackbar('Failed to save updated part position to server.', { variant: 'error' });
      }
    }

    // The re-nest request carries the edit too, so it still runs when the save failed.
    void submitRenestTrigger({
      type: 'edit',
      editedPartId: finalPartId,
      newTransformMatrix: swap.finalMatrix,
      replacedOldPartId: replacedSlot?.oldPartId || null,
      editedOrientationIndex: finalOrientationIndex,
      payload: lockedPayload,
    });

    if (isSaved) {
      enqueueSnackbar(replacedSlot ? 'Part replaced.' : 'Part position updated.', { variant: 'success' });
    }
  }, [
    cancelOrientationAnimation,
    editPartRef,
    exitEditMode,
    replacedSlotInfoRef,
    editGridRef,
    editCurrentOrientationIndexRef,
    buildUpdatedNestedParts,
    submitRenestTrigger,
    layerData,
    setLayerData,
    currentLayer,
    scenePartsMap,
    palletChildrenMap,
    setPalletChildrenMap,
    totalSteps,
    setTotalSteps,
    onStepChange,
    currentLoadingPlan,
    updateCurrentLoadingPlan,
  ]);

  const handleAcceptEdit = useCallback(() => {
    if (editIsIntersectingRef.current) {
      setIsInvalidPlacementDialogOpen(true);
      return;
    }
    void performAccept();
  }, [editIsIntersectingRef, performAccept]);

  const handleDeclineEdit = useCallback(() => {
    cancelOrientationAnimation(false);
    exitEditMode(true);
  }, [cancelOrientationAnimation, exitEditMode]);

  useEffect(() => {
    if (!viewer) {
      initialize(config.viewer.canvasId);
    }
  }, [viewer, initialize]);

  useEffect(() => {
    viewer?.animate();
  }, [viewer]);

  useEffect(() => {
    return () => {
      initialize('');
    };
  }, [initialize]);

  return (
    <>
      <div className='viewerContainer'>
        <canvas id={config.viewer.canvasId} />
        {isLoadingParts && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000,
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading parts...
            </Typography>
          </Box>
        )}
        {isRecalculating && !isLoadingParts && (
          // Non-blocking background recalculation indicator.
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              boxShadow: 1,
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Nesting next parts...
            </Typography>
          </Box>
        )}
      </div>
      {showNavigation && totalSteps > 0 && (
        <Box className="viewer-edit-controls">
          {!isEditMode ? (
            <Tooltip title="Move, rotate or replace the highlighted part" placement="right">
              <span>
                <Button
                  className="edit-entry-button"
                  variant="contained"
                  size="small"
                  startIcon={
                    isLoadingPartsOrientations ? <CircularProgress size={14} color="inherit" /> : <EditOutlinedIcon />
                  }
                  disabled={isLoadingPartsOrientations}
                  onClick={handleEditClick}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Paper className="edit-mode-panel" elevation={3}>
              <Typography variant="caption" className="edit-mode-header">
                Editing: {currentPartDbMeta?.name || activePartId}
              </Typography>

              <Stack direction="row" className="edit-mode-orientation">
                <Tooltip title="Previous orientation">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOrientationPrev}
                      disabled={hasEngineOrientations && editOrientationTotal <= 1}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="caption" className="edit-mode-orientation-label">
                  {hasEngineOrientations
                    ? `Orientation ${editOrientationStep + 1} of ${editOrientationTotal}`
                    : `Orientation ${editOrientationAngle}°`}
                </Typography>
                <Tooltip title="Next orientation" placement="right">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleOrientationNext}
                      disabled={hasEngineOrientations && editOrientationTotal <= 1}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Tooltip
                title={
                  isCurrentPartSupport
                    ? 'Dunnage and pallets cannot be replaced'
                    : 'Replace this part with another part'
                }
                placement="right"
              >
                <span>
                  <Button
                    className="edit-mode-replace"
                    variant="outlined"
                    color="inherit"
                    size="small"
                    fullWidth
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setIsReplaceModalOpen(true)}
                    disabled={isCurrentPartSupport}
                  >
                    Replace
                  </Button>
                </span>
              </Tooltip>

              <Stack direction="row" spacing={0.75} className="edit-mode-actions">
                <Button
                  variant="contained"
                  color={isPartIntersecting ? 'warning' : 'success'}
                  size="small"
                  fullWidth
                  startIcon={<CheckIcon />}
                  onClick={handleAcceptEdit}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  fullWidth
                  startIcon={<CloseIcon />}
                  onClick={handleDeclineEdit}
                >
                  Cancel
                </Button>
              </Stack>

              {/* The engine rejects a cell for any of these reasons; the panel is too narrow to list them inline. */}
              <Tooltip
                title={
                  !isLoadingPartPositions && isPartIntersecting
                    ? isOrientationUnfit
                      ? ORIENTATION_UNFIT_REASON
                      : INVALID_POSITION_REASONS
                    : ''
                }
                placement="right"
              >
                <Box className="edit-mode-status">
                  {isLoadingPartPositions ? (
                    <>
                      <CircularProgress size={13} thickness={5} color="inherit" className="status-icon" />
                      <Typography variant="caption" className="status-text">
                        Checking positions…
                      </Typography>
                    </>
                  ) : isPartIntersecting ? (
                    <>
                      <WarningAmberIcon fontSize="small" color="error" className="status-icon" />
                      <Typography variant="caption" color="error" className="status-text">
                        {isOrientationUnfit ? "Orientation won't fit" : 'Position not available'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <OpenWithIcon fontSize="small" className="status-icon" />
                      <Typography variant="caption" className="status-text">
                        Drag a handle to move
                      </Typography>
                    </>
                  )}
                </Box>
              </Tooltip>
            </Paper>
          )}
        </Box>
      )}
      {showNavigation && (
        <Box className="navigation-controls">
          <IconButton onClick={handlePrevStep} disabled={currentStep === 0 || isEditMode}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {currentStep + 1}/{totalSteps}
          </Typography>
          <IconButton onClick={() => handleNextStep()} disabled={isStepChanging || totalSteps === 0 || isEditMode}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      )}
      <ReplacePartModal
        open={isReplaceModalOpen}
        onClose={() => setIsReplaceModalOpen(false)}
        onConfirm={handleConfirmReplace}
        currentPartId={activePartId}
        currentPartName={currentPartDbMeta?.name || activePartId || undefined}
        currentPartOrderNumber={currentPartNestedInfo?.order_number}
        currentStepNumber={currentStep + 1}
        nestedParts={currentLoadingPlan?.nestingOutput?.nested_parts}
        includedParts={currentLoadingPlan?.includedParts}
        excludedParts={currentLoadingPlan?.nestingOutput?.excluded_parts}
      />
      <InvalidPlacementDialog
        open={isInvalidPlacementDialogOpen}
        onCancel={() => setIsInvalidPlacementDialogOpen(false)}
        onProceed={() => {
          setIsInvalidPlacementDialogOpen(false);
          void performAccept();
        }}
      />
    </>
  );
}
